import { selectBookmarkedFilterStatus, selectMasterTransactionsTableCurrentTab } from './../reducers/index';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { StatusViewService } from '../../shared/services/status-view.service';
import { AchActions, CheckActions, InternalTransferActions, MasterStatusViewActions, MasterTransactionsTableActions, WireActions } from '../actions';
import { selectMasterAccount } from '../../account-info-header/reducers';
import { MsvTransactionAttributes, MsvTransactionResponse } from '../models/master-status-view/TransactionsResponse';
import { ServiceError } from '../models/shared/ServiceError';
import { InternalTransferInquiryRequest } from '../models/internal-transfer/InternalTransferInquiryRequest';
import { RequestTransactionType, ViewType } from '../../shared/utilities/Constants';
import { InternalTransferInquiryResponse } from '../models/internal-transfer/InternalTransferInquiryResponse';
import { isStandingInstruction } from '../../shared/types/StandingInstruction';
import { CheckInquiryRequest } from '../models/check/CheckInquiryRequest';
import { CheckInquiryResponse } from '../models/check/CheckInquiryResponse';
import { AchInquiryResponse } from '../models/ach/AchInquiryResponse';
import { WireInquiryResponse } from '../models/wire/WireInquiryResponse';
import { StandingAuthMasterStatusViewActions } from '../../standing-authorizations/actions';
import { MsvLetterAttributes } from '../models/Standing-Authorizations';
import { JsonApiListWrap } from '../../shared/models/JsonApiWrap';
import { selectMasterSABookmarkedFilterStatus } from '../../standing-authorizations/reducers';
import { selectCreateTypeInContext } from '../../shared/reducers';

@Injectable()
export class MasterStatusViewEffects {
  loadMasterAccountInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MasterStatusViewActions.getMasterTransactionsList,
        MasterStatusViewActions.refreshMaster,
        MasterTransactionsTableActions.currentTabUpdated,
        MasterStatusViewActions.getBookmarkedAccounts,
        // listen to msv standing auth table load to cancel in progress transactions table load request
        StandingAuthMasterStatusViewActions.callLetters
      ),
      concatLatestFrom(() => [
        this.store.select(selectMasterAccount),
        this.store.select(selectMasterTransactionsTableCurrentTab),
        this.store.select(selectBookmarkedFilterStatus),
        this.store.select(selectCreateTypeInContext)
      ]),
      switchMap(([action, masterAccountId, currentTab, isBookmarkedFilterOn, headerTabInContext]) => {
        masterAccountId = masterAccountId.padStart(8, '0');
        if (currentTab !== 'Activity Report') {
          let isRecurring = currentTab === 'Recurring';
          // check L2 tab in context to prevent calling both endpoints
          if (headerTabInContext === 'transaction')
          {
            return this.statusViewService
            .getMasterAccountTransactions(masterAccountId, isRecurring, isBookmarkedFilterOn)
            .pipe(
              map((response: MsvTransactionResponse) => {
                if (response.data.length >= 1) {
                  if (isBookmarkedFilterOn) {
                    return MasterStatusViewActions.callBookmarkedAccountsSuccess(response);
                  } else {
                    return MasterStatusViewActions.callTransactionsSuccess(response);
                  }
                } else {
                  return MasterTransactionsTableActions.hasEmptyResponse();
                }
              }),
              catchError((err: ServiceError) => {
                return of(MasterStatusViewActions.callTransactionsFailure(err));
              })
            );
          }
        }
        return EMPTY;
      })
    )
  );

  loadSaMasterAccountInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        StandingAuthMasterStatusViewActions.callLetters,
        StandingAuthMasterStatusViewActions.refreshSaMaster,
        StandingAuthMasterStatusViewActions.getBookmarkedAccounts,
        // listen to msv transaction status table load to cancel in progress msv standing auth table load
        MasterStatusViewActions.getMasterTransactionsList
      ),
      concatLatestFrom(() => [
        this.store.select(selectMasterAccount),
        this.store.select(selectMasterSABookmarkedFilterStatus),
        this.store.select(selectCreateTypeInContext)
      ]),
      switchMap(([_action, masterAccountId, isBookmarkedFilterOn, headerTabInContext]) => {
          masterAccountId = masterAccountId.padStart(8, '0');

          // check L2 tab in context to prevent calling both endpoints
          if (headerTabInContext === 'standing-authorization')
          {
            return this.statusViewService
            .getMasterAccountStandingAuthorizations(masterAccountId, isBookmarkedFilterOn)
            .pipe(
              map((response: JsonApiListWrap<MsvLetterAttributes>) => {
                if (response.data.length >= 1) {
                  if (isBookmarkedFilterOn) {
                    return StandingAuthMasterStatusViewActions.callBookmarkedAccountsSuccess(response);
                  } else {
                    return StandingAuthMasterStatusViewActions.callLettersSuccess(response);
                  }
                } else {
                  return StandingAuthMasterStatusViewActions.callLettersEmptyResponse();
                }
              }),
              catchError((err: ServiceError) => {
                return of(StandingAuthMasterStatusViewActions.callLettersFailure(err));
              })
            );
          }
          return EMPTY;
      })
    )
  );

  callInternalTransferInquiryMsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterStatusViewActions.callMsvInternalTransferInquiry),
      switchMap((action) => {
        let payload: InternalTransferInquiryRequest = {
          cashieringRequestId: parseInt(
            action.payload.attributes.cashieringRqstId
          ),
          cashieringRqstSecondId: parseInt(
            action.payload.attributes.cashieringRqstSecondId
          ),
          isFromView: true,
          subAccountId: action.payload.attributes.accountNumber.toString(),
          masterAccountId:
            action.payload.attributes.faMasterAcct.toString().padStart(8, '0'),
          transactionType: this.getTransactionType(
            action.viewType,
            action.payload.attributes
          ),
        };
        return this.statusViewService.callInternalInquiry(payload).pipe(
          map(
            (
              internalTransferInquiryResponse: InternalTransferInquiryResponse
            ) => {
              return InternalTransferActions.inquiryCallSuccessful({
                internalTransferInquiryResponse,
              });
            }
          ),
          catchError((err: ServiceError) =>
            of(InternalTransferActions.inquiryCallFail(err))
          )
        );
      })
    )
  );

  callSaInternalTransferInquiryMsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthMasterStatusViewActions.callSaMsvInternalTransferInquiry),
      switchMap((action) => {
        let payload: InternalTransferInquiryRequest = {
          cashieringRequestId: parseInt(
            action.payload.cashieringRqstId
          ),
          cashieringRqstSecondId: parseInt(
            action.payload.cashieringRqstSecondId
          ),
          isFromView: true,
          subAccountId: action.payload.accountNumber.toString(),
          masterAccountId:
            action.payload.faMasterAcct.toString().padStart(8, '0'),
          transactionType: this.getTransactionType(
            action.viewType,
            action.payload
          ),
        };
        return this.statusViewService.callInternalInquiry(payload).pipe(
          map(
            (
              internalTransferInquiryResponse: InternalTransferInquiryResponse
            ) => {
              return InternalTransferActions.inquiryCallSuccessful({
                internalTransferInquiryResponse,
              });
            }
          ),
          catchError((err: ServiceError) =>
            of(InternalTransferActions.inquiryCallFail(err))
          )
        );
      })
    )
  );

  callMsvCheckInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterStatusViewActions.callMsvCheckInquiry),
      switchMap((action) => {
        let isIncoming = action.payload.attributes.outIn.includes('Incoming');
        let payload: CheckInquiryRequest = {
          cashieringRequestId: action.payload.attributes.cashieringRqstId,
          cashieringRqstSecondId:
            action.payload.attributes.cashieringRqstSecondId,
          isFromView: true,
          isIncoming:isIncoming,
          subAccountId: action.payload.attributes.accountNumber,
          masterAccountId: action.payload.attributes.faMasterAcct
            .toString()
            .padStart(8, '0'),
          isCheckSloa: false,
        };

        return this.statusViewService.callCheckInquiry(payload).pipe(
          map((checkInquiryResponse: CheckInquiryResponse) => {
            if (!checkInquiryResponse?.cashieringCheckInputDetails) {
              let err: ServiceError = <ServiceError>{
                message: 'Inquiry call failed',
              };
              return CheckActions.inquiryCallFailed(err);
            }
            return isIncoming
              ? CheckActions.incomingInquiryCallSuccessful({
                  checkInquiryResponse,
                })
              : CheckActions.inquiryCallSuccessful({ checkInquiryResponse });
          }),
          catchError((err: ServiceError) =>
            of(CheckActions.inquiryCallFailed(err))
          )
        );
      })
    )
  );

  callMsvCheckInquiryForStandingAuth$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StandingAuthMasterStatusViewActions.callSaMsvCheckInquiry),
        switchMap((action) => {
          let isIncoming: boolean = false;
          if ((action.payload.outIn) || isStandingInstruction(action.payload)) {
            isIncoming = action.payload.outIn === 'Incoming';
          }
          let payload: CheckInquiryRequest = {
            cashieringRequestId: action.payload.cashieringRqstId,
            cashieringRqstSecondId: action.payload.cashieringRqstSecondId,
            isFromView: true,
            isIncoming: action.payload.outIn === 'Incoming',
            subAccountId: action.payload.accountNumber,
            masterAccountId: action.payload.faMasterAcct.toString().padStart(8, '0'),
            isCheckSloa: true
          };

          return this.statusViewService.callCheckInquiry(payload).pipe(
            map((checkInquiryResponse: CheckInquiryResponse) => {
              if (!checkInquiryResponse?.cashieringCheckInputDetails) {
                let err: ServiceError = <ServiceError>{ message: 'Inquiry call failed' }
                return CheckActions.inquiryCallFailed(err);
              }
              return isIncoming ? CheckActions.incomingInquiryCallSuccessful({ checkInquiryResponse }) : CheckActions.inquiryCallSuccessful({ checkInquiryResponse });
            }),
            catchError((err: ServiceError) => of(CheckActions.inquiryCallFailed(err))
            )
          );
        })
      )
    );

  callMsvAchInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MasterStatusViewActions.callMsvAchInquiry),
      switchMap((action) => {
        let payload: any = {
          cashieringRequestId: action.payload.attributes.cashieringRqstId,
          cashieringRqstSecondId:
            action.payload.attributes.cashieringRqstSecondId,
          isFromView: true,
          subAccountId: action.payload.attributes.accountNumber,
          masterAccountId: action.payload.attributes.faMasterAcct
            .toString()
            .padStart(8, '0'),
          isReactivate: false,
          recTypeCode: action.payload.attributes.txnCd,
          thirdPartyIndicator: '',
        };

        return this.statusViewService.callAchInquiry(payload).pipe(
          map((achInquiryResponse: AchInquiryResponse) => {
            return AchActions.inquiryCallSuccessful({ achInquiryResponse });
          }),
          catchError((err: ServiceError) =>
            of(AchActions.inquiryCallFailed(err))
          )
        );
      })
    )
  );
  
  callMsvAchInquiryForStandingAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthMasterStatusViewActions.callSaMsvAchInquiry),
      switchMap((action) => {
        let payload: any = {
          recTypeCode: 'ASC',
          isReactivate: false,
          thirdPartyIndicator: '',
          cashieringRequestId: action.payload.cashieringRqstId,
          cashieringRqstSecondId: action.payload.cashieringRqstSecondId,
          subAccountId: action.payload.accountNumber,
          masterAccountId: action.payload.faMasterAcct
            .toString()
            .padStart(8, '0'),
          isFromView: true,          
        };

        return this.statusViewService.callAchInquiry(payload).pipe(
          map((achInquiryResponse: AchInquiryResponse) => {
            return AchActions.inquiryCallSuccessful({ achInquiryResponse });
          }),
          catchError((err: ServiceError) =>
            of(AchActions.inquiryCallFailed(err))
          )
        );
      })
    )
  );

  callMsvWireInquiry$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MasterStatusViewActions.callMsvWireInquiry),
        switchMap((action) => {
          let payload: any = {
            cashieringRequestId: action.payload.attributes.cashieringRqstId,
            cashieringRqstSecondId: action.payload.attributes.cashieringRqstSecondId,
            subAccountId: action.payload.attributes.accountNumber,
            masterAccountId: action.payload.attributes.faMasterAcct.toString().padStart(8, '0'),
            transactionType: action.transactionType
          };

          let isThirdParty: boolean = false;
          if (isStandingInstruction(action.payload)) {
            isThirdParty = action.payload.isThirdParty;
          } else {
            isThirdParty = action.payload.attributes.isThirdPartyInd;
          }

          return this.statusViewService.callWireInquiry(payload).pipe(
            map((wireInquiryResponse: WireInquiryResponse) => {
              return WireActions.inquiryCallSuccessful({ wireInquiryResponse: { ...wireInquiryResponse, isThirdPartyTransaction: isThirdParty } });
            }),
            catchError((err: ServiceError) => of(WireActions.inquiryCallFailed(err))
            )
          );
        })
      )
  );  
  
  callMsvWireInquiryForStandingAuth$ = createEffect(() =>
      this.actions$.pipe(
        ofType(StandingAuthMasterStatusViewActions.callSaMsvWireInquiry),
        switchMap((action) => {
          let payload: any = {
            transactionType: action.transactionType,
            cashieringRequestId: action.payload.cashieringRqstId,
            cashieringRqstSecondId: action.payload.cashieringRqstSecondId,
            subAccountId: action.payload.accountNumber,
            masterAccountId: action.payload.faMasterAcct.toString().padStart(8, '0'),
            isFromView: true
          };

          let isThirdParty: boolean = false;
          isThirdParty = action.payload.isThirdPartyInd;

          return this.statusViewService.callWireInquiry(payload).pipe(
            map((wireInquiryResponse: WireInquiryResponse) => {
              return WireActions.inquiryCallSuccessful({ wireInquiryResponse: { ...wireInquiryResponse, isThirdPartyTransaction: isThirdParty } });
            }),
            catchError((err: ServiceError) => of(WireActions.inquiryCallFailed(err))
            )
          );
        })
      )
  );

  constructor(
    private actions$: Actions,
    private statusViewService: StatusViewService,
    private store: Store
  ) {}

  getTransactionType(viewType: string, payload: MsvTransactionAttributes) {
    if (
      (payload.txnCd && payload.txnCd.charAt(1) === 'T') ||
      viewType === ViewType.standingAuth
    ) {
      return RequestTransactionType.JournalSloa;
    } else if (payload.txnCd && payload.txnCd.charAt(1) === 'E') {
      return RequestTransactionType.JournalOneTime;
    } else {
      return RequestTransactionType.JournalRecurring;
    }
  }
}
